// Small pieces shared by the Voice engines tabs.

import type { ReactNode } from "react";
import type { DryRun, LadderStep } from "../../services/voiceEngines";
import type { DiffLine } from "./policy";
import { clientText, decisionWhy, engineLabel, profileText } from "./labels";

export type Tone = "ok" | "warn" | "error" | "info";

export function Callout({
  tone,
  badge,
  children,
  role,
}: {
  tone: Tone;
  badge: string;
  children: ReactNode;
  role?: "status" | "alert";
}) {
  return (
    <div className={`ve-callout ${tone}`} role={role}>
      <span className={`badge ve-${tone}`}>{badge}</span>
      <div>{children}</div>
    </div>
  );
}

// A list of messages in a callout; nothing when empty.
export function Messages({ tone, badge, items }: { tone: Tone; badge: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <Callout tone={tone} badge={badge} role={tone === "error" ? "alert" : undefined}>
      {items.length === 1 ? (
        items[0]
      ) : (
        <ul>
          {items.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
    </Callout>
  );
}

export function DiffList({ rows }: { rows: DiffLine[] }) {
  return (
    <ul className="ve-diff">
      {rows.map((r) => (
        <li key={r.field}>
          <span className="mono field">{r.field}</span>
          <span className="mono from">{r.from}</span>
          <span className="muted" aria-hidden="true">→</span>
          <span className="mono to">{r.to}</span>
        </li>
      ))}
    </ul>
  );
}

const OUTCOME_TAG: Record<LadderStep["outcome"], string> = {
  passed: "no",
  decided: "decides",
  not_reached: "not reached",
};

export function Ladder({ steps }: { steps: LadderStep[] }) {
  return (
    <ol className="ve-ladder" aria-label="Resolution ladder">
      {steps.map((s) => (
        <li key={s.step} className={s.outcome}>
          <span className="mono n">{s.step}</span>
          <span className="what">
            {s.label}
            <span className="muted ve-small"> · {s.detail}</span>
          </span>
          <span className="tag">{OUTCOME_TAG[s.outcome]}</span>
        </li>
      ))}
    </ol>
  );
}

// A dry run: the engine and profile, why, and the ladder that decided it.
export function DryRunResult({ run, who }: { run: DryRun; who?: string | null }) {
  const d = run.decision;
  return (
    <>
      <div className="ve-result">
        <div className="muted ve-small">
          {who ? `${who} · ` : ""}uid <span className="mono">{run.uid}</span> · {clientText(run.client)} · bucket{" "}
          {run.bucket}
        </div>
        <div className="ve-row" style={{ marginTop: 6, alignItems: "baseline" }}>
          <span className="ve-result-engine">{engineLabel(d.engine)}</span>
          <span className="mono">{profileText(d.profileKey, d.profileVersion)}</span>
        </div>
        <div className="chip-row">
          <span className="chip">
            arm <span className="mono">{d.arm ?? "none"}</span>
          </span>
          <span className="chip">
            engine_source <span className="mono">{d.source}</span>
          </span>
        </div>
        {d.reason && <div className="ve-small" style={{ color: "var(--warn)", marginTop: 8 }}>{decisionWhy(d)}</div>}
      </div>
      <Ladder steps={run.ladder} />
    </>
  );
}
